import { createCustomer } from "@/apis/tms/customer";
import { useGlobalContext } from "@/hooks/context";
import { DialogRef, TMSCustomerVo, TMSNewCustomerDo } from "@/types";
import { Button, Form, Input } from "antd";

import { Modal } from "antd";
import { Rule } from "antd/es/form";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";

interface NewCustomerDialogProps {
  onCreated: (customer: TMSCustomerVo) => void;
}

const NewCustomerFormFieldRules: Record<keyof TMSNewCustomerDo, Rule[]> = {
  name: [{ required: true, message: "Customer name is required" }],
  companyName: [{ required: true, message: "Company name is required" }],
  sscc: [
    { required: false },
    // only numbers, can only contain 7 - 12 digits
    { pattern: /^[0-9]{7,11}$/, message: "SSCC must be 7 - 12 digits" },
  ],
  ssccAmazonPrefix: [
    { required: false },
    // only numbers, 7 - 12 digits
    { pattern: /^[0-9]{7,11}$/, message: "SSCC must be 7 - 12 digits" },
  ],
  ssccBestbuyPrefix: [
    { required: false },
    // only numbers, 7 - 12 digits
    { pattern: /^[0-9]{7,11}$/, message: "SSCC must be 7 - 12 digits" },
  ],
  ssccTargetPrefix: [
    { required: false },
    // only numbers, 7 - 12 digits
    { pattern: /^[0-9]{7,11}$/, message: "SSCC must be 7 - 12 digits" },
  ],
};

const NewCustomerDialog = forwardRef<DialogRef, NewCustomerDialogProps>(
  (props, ref) => {
    const { hintPrompt } = useGlobalContext();
    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const openDialog = useCallback(() => {
      setIsModalOpen(true);
      setLoading(false);
    }, []);

    const closeDialog = useCallback(() => {
      setIsModalOpen(false);
      form.resetFields();
      setLoading(false);
    }, [form]);

    const submitCustomer = useCallback(() => {
      setLoading(true);
      form
        .validateFields()
        .then(async (values) => {
          return createCustomer(values)
            .then((customer) => {
              closeDialog();
              hintPrompt(
                "success",
                "Success",
                `Customer ${customer.name} created successfully`,
              );
            })
            .catch((error) => {
              hintPrompt("error", "Failed to create customer", error.message);
            });
        })
        .catch((error) => {
          console.error(error);
          hintPrompt("error", "Failed to create customer", error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }, [form, hintPrompt, closeDialog]);

    useImperativeHandle(ref, () => ({
      showModal: openDialog,
      hideModal: closeDialog,
    }));

    return (
      <Modal
        title="Create New Customer"
        open={isModalOpen}
        onCancel={closeDialog}
        footer={null}
        maskClosable={false}
        loading={loading}
      >
        <Form form={form} layout={"vertical"}>
          <Form.Item
            label="Customer Name"
            name="name"
            rules={NewCustomerFormFieldRules.name}
            required
            normalize={(v) => (v ?? "").toUpperCase()}
          >
            <Input style={{ textTransform: "uppercase" }} />
          </Form.Item>
          <Form.Item
            label="Company Name"
            name="companyName"
            rules={NewCustomerFormFieldRules.companyName}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="SSCC Customer NO."
            name="sscc"
            rules={NewCustomerFormFieldRules.sscc}
            required
          >
            <Input placeholder="Customer SSCC No." />
          </Form.Item>

          <Form.Item
            label="Target SSCC"
            name="ssccTargetPrefix"
            rules={NewCustomerFormFieldRules.ssccTargetPrefix}
          >
            <Input placeholder="Customized Target SSCC No." />
          </Form.Item>

          <Form.Item
            label="Amazon SSCC"
            name="ssccAmazonPrefix"
            rules={NewCustomerFormFieldRules.ssccAmazonPrefix}
          >
            <Input placeholder="Customized Amazon SSCC No." />
          </Form.Item>

          <Form.Item
            label="BestBuy SSCC"
            name="ssccBestbuyPrefix"
            rules={NewCustomerFormFieldRules.ssccBestbuyPrefix}
          >
            <Input placeholder="Customized BestBuy SSCC No." />
          </Form.Item>
        </Form>
        <div className="flex flex-row gap-2 justify-end pt-0">
          <Button type="primary" onClick={submitCustomer} loading={loading}>
            Create
          </Button>
          <Button onClick={closeDialog}>Cancel</Button>
        </div>
      </Modal>
    );
  },
);

export default NewCustomerDialog;
